import React from "react";
import PostItemContainer from "./PostItem.container";
import LoadingComponent from "commons/loading/Loading.component";
import { PAGE_TYPE } from "enums";
import { getLanguageValue } from "locales/i18n";
import { IBbs } from "api/interfaces/bbs.interface";
import { Link } from "react-router-dom";

// 포스트 스켈레톤 컴포넌트
const PostItemSkeleton = () => (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden animate-pulse">
        {/* 포스트 헤더 스켈레톤 */}
        <div className="p-4 sm:p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    {/* 프로필 이미지 스켈레톤 */}
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0"></div>
                    <div className="space-y-2">
                        {/* 닉네임 스켈레톤 */}
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                        {/* 시간 스켈레톤 */}
                        <div className="h-3 bg-gray-200 rounded w-20"></div>
                    </div>
                </div>
                {/* 메뉴 버튼 스켈레톤 */}
                <div className="w-6 h-6 bg-gray-200 rounded"></div>
            </div>
        </div>

        {/* 포스트 내용 스켈레톤 */}
        <div className="p-4 sm:p-6">
            {/* 텍스트 내용 스켈레톤 */}
            <div className="mb-4 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                <div className="h-4 bg-gray-200 rounded w-4/6"></div>
            </div>

            {/* 이미지 스켈레톤 */}
            <div className="mb-4">
                <div className="flex gap-2">
                    <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0"></div>
                    <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0"></div>
                    <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0"></div>
                </div>
            </div>

            {/* 메타 정보 스켈레톤 */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-6">
                    {/* 좋아요 스켈레톤 */}
                    <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded w-8"></div>
                    </div>
                    {/* 댓글 스켈레톤 */}
                    <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded w-8"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

// 타임라인 스켈레톤 컴포넌트
const TimelineSkeleton = () => (
    <div className="space-y-6">
        {[1, 2, 3].map((index) => (
            <PostItemSkeleton key={index} />
        ))}
    </div>
);

export const TimelineComponent = (props: any) => {
    const { setPosts, posts, isLoadingPosts, isLoadingMore, hasMore, handleLoadMore, title, handlePostWritingPopup, info } = props;

    // 2026년 2월 17일까지만 배너 표시
    const showFortuneBanner = () => {
        const today = new Date();
        const endDate = new Date('2026-02-17T23:59:59');
        return today <= endDate;
    };

    return (
        <>
            <div className="mx-auto space-y-6 px-2">
                {/* 2026, 내 캐스팅 운세는? 알림 영역 */}
                {showFortuneBanner() && (
                <Link 
                    to="/student/hub/event/2026-fortune-telling"
                    className="flex items-center justify-between mb-4 p-0 bg-white/90 rounded-lg shadow-sm hover:bg-white transition-colors cursor-pointer"
                >
                    <div className="flex items-center flex-1">
                        <img 
                            alt="casting fortune icon" 
                            src="/imgs/event/icon-saju.png" 
                            className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0"
                        />
                        <p className="text-sm sm:text-base text-gray-800 font-medium ml-3 sm:ml-4 text-left">
                            {getLanguageValue(PAGE_TYPE.COMMON, "home.txtFortuneTitle")}
                        </p>
                    </div>
                    <svg 
                        className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 flex-shrink-0 mr-3 sm:mr-4" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </Link>
                )}

                {/* 포스트 작성 폼 */}
                <div className="bg-white p-4 rounded-2xl shadow-sm mb-4">
                    {info?.myPostCount === 0 &&
                        <div className="bg-[#FFD700] p-4 rounded-2xl shadow-sm mb-4">
                            <div className="flex items-center space-x-3">
                                <div className="rounded-full flex items-center justify-center flex-shrink-0">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-700">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "home.txtNoticeWritePost")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    }

                    <div className="space-y-4">
                        <div className="flex items-center gap-2">
                            <svg className="w-6 h-6 text-[#CF3688]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {getLanguageValue(PAGE_TYPE.STUDENT, "hubCommunity.txtPostWriteTitle")}
                            </h3>
                        </div>
                        <input type="text" className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl
                                                     focus:!border-[#CF3688] 
                                                    text-base transition-colors"
                            placeholder={getLanguageValue(PAGE_TYPE.STUDENT, "hubCommunity.txtPostWriteInputPlaceholder")}
                            onClick={handlePostWritingPopup}
                            readOnly={true}
                        // maxLength={32}
                        // value={subject}
                        // onChange={onSubjectChange}
                        />
                    </div>
                </div>
            </div>

            <div className="space-y-4 px-4">
                {/* 초기 로딩 시 스켈레톤 표시 */}
                {isLoadingPosts ? (
                    <TimelineSkeleton />
                ) : (
                    <>
                        {posts.map((post: IBbs) => (
                            <PostItemContainer
                                key={post.id}
                                post={post}
                                setPosts={setPosts}
                                posts={posts}
                            />
                        ))}

                        {posts.length === 0 && !isLoadingPosts && (
                            <div className="flex flex-col justify-center items-center h-32">
                                <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                </svg>
                                <div className="text-gray-500">
                                    Empty
                                </div>
                            </div>
                        )}

                        {/* 무한 스크롤 로딩 인디케이터 */}
                        {isLoadingMore && (
                            <div className="space-y-4">
                                {[...Array(2)].map((_, index) => (
                                    <PostItemSkeleton key={`loading-${index}`} />
                                ))}
                            </div>
                        )}

                        {/* 더 이상 데이터가 없을 때 표시 */}
                        {!hasMore && posts.length > 0 && (
                            <div className="flex justify-center items-center py-8">
                                <div className="text-center">
                                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    {/* <p className="text-gray-500 text-sm">모든 포스트를 불러왔습니다</p> */}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default TimelineComponent;